import { effect, signal } from "@preact/signals";
import * as preact from "preact";

import * as modalController from "modalController";
import * as premiumUpsellModal from "premiumUpsellModal";
import readlangClient from "readlangClient";
import { cn } from "shared/utils";
import stores from "stores/stores";

export interface ContextTranslationUsage {
  remaining: number;
  limit: number;
}

export const contextTranslationUsageSignal =
  signal<ContextTranslationUsage | null>(null);

interface Props {
  initialUsage?: ContextTranslationUsage;
  language: string;
}

interface State {
  remaining: number | null;
  limit: number;
  loaded: boolean;
}

export default class ContextTranslationStrip extends preact.Component<
  Props,
  State
> {
  disposeUsageEffect?: () => void;
  previousRemaining: number | null = null;

  constructor(props: Props) {
    super(props);
    this.previousRemaining = props.initialUsage?.remaining ?? null;
    this.state = {
      remaining: props.initialUsage?.remaining ?? null,
      limit: props.initialUsage?.limit ?? 10,
      loaded: props.initialUsage !== undefined,
    };
  }

  componentDidMount() {
    this.disposeUsageEffect = effect(() => {
      const usage = contextTranslationUsageSignal.value;
      if (usage === null) return;
      this.onUsageUpdate(usage);
    });
    if (!this.props.initialUsage) {
      this.fetchUsage();
    }
  }

  componentWillUnmount() {
    this.disposeUsageEffect?.();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.initialUsage !== this.props.initialUsage) {
      if (this.props.initialUsage) {
        this.onUsageUpdate(this.props.initialUsage);
      } else {
        this.fetchUsage();
      }
      return;
    }
    if (prevProps.language !== this.props.language) {
      this.fetchUsage();
    }
  }

  onUsageUpdate = (usage: ContextTranslationUsage) => {
    const previousRemaining = this.previousRemaining;
    this.previousRemaining = usage.remaining;
    this.setState({
      remaining: usage.remaining,
      limit: usage.limit,
      loaded: true,
    });
    if (
      previousRemaining !== null &&
      previousRemaining > 0 &&
      usage.remaining === 0
    ) {
      this.showExhaustedTooltip();
    }
  };

  fetchUsage = async () => {
    try {
      const response = await readlangClient.request(
        "GET",
        "/api/translate/contextUsage",
        { source: this.props.language },
        undefined,
        undefined
      );
      const { remaining, limit, eligible } = response.data;
      if (eligible) {
        this.previousRemaining = remaining;
        this.setState({ remaining, limit, loaded: true });
      }
    } catch (_error) {
      // Silent failure — strip simply stays hidden
    }
  };

  showLearnMore = () => {
    const { remaining, limit } = this.state;
    const exhausted = remaining === 0;
    premiumUpsellModal.show({
      title: "contextTranslations",
      heading: exhausted
        ? "You're out of free context-aware translations for today"
        : `You have ${remaining ?? 0}/${limit} free context-aware translations left today`,
      subheading: exhausted
        ? "Carry on with basic translations or upgrade for unlimited context-aware translations."
        : "After that, translations will not take the surrounding context into account. For unlimited context-aware translations, upgrade now.",
      showTitle: false,
    });
  };

  isToggleOn = (): boolean => {
    // The strip is shown to free experiment users, for whom
    // `hasFeature("contextTranslations")` always returns false because the flag
    // is gated to `premium`. Read the raw stored flag instead so the toggle
    // reflects what the backend actually uses.
    return stores.user.featureFlags.value.contextTranslations ?? true;
  };

  onToggleClick = () => {
    const exhausted = this.state.remaining === 0;

    if (exhausted) {
      this.showLearnMore();
      return;
    }

    stores.user.setFeature("contextTranslations", !this.isToggleOn());
  };

  showExhaustedTooltip = () => {
    const element = document.createElement("div");
    element.className = "contextTranslationExhaustedTooltip";
    element.innerHTML = `
      <p>You're out of free context-aware translations for today.</p>
      <div class="contextTranslationExhaustedTooltip__buttons">
        <button type="button" class="contextTranslationExhaustedTooltip__upgrade">Upgrade for unlimited</button>
        <button type="button" class="contextTranslationExhaustedTooltip__continue">Continue with basic translations</button>
      </div>
    `;

    element
      .querySelector(".contextTranslationExhaustedTooltip__upgrade")
      .addEventListener("click", () => {
        modalController.cancel();
        window.location.href = "/checkout";
      });

    element
      .querySelector(".contextTranslationExhaustedTooltip__continue")
      .addEventListener("click", () => {
        modalController.cancel();
      });

    const container = document.getElementById(
      "contextTranslationStripContainer"
    );
    if (!container) return;

    // Position above the strip with fixed positioning so the tooltip
    // lives on document.body and is not trapped inside the strip's
    // stacking context (which sits below the modal backdrop).
    const rect = container.getBoundingClientRect();
    element.style.position = "fixed";
    element.style.bottom = `${window.innerHeight - rect.top + 12}px`;
    element.style.left = `${rect.left + rect.width / 2}px`;
    element.style.transform = "translateX(-50%)";
    document.body.append(element);

    modalController.showLegacy(element, {
      center: false,
      closeButton: false,
      title: "Context translations exhausted",
      cancelCallback: () => {
        element.remove();
      },
    });
  };

  onLearnMoreClick = (event: Event) => {
    event.preventDefault();
    this.showLearnMore();
  };

  render() {
    if (stores.user.hasPremiumAccess.value) {
      return null;
    }

    if (!this.state.loaded || this.state.remaining === null) {
      return null;
    }

    const { remaining, limit } = this.state;
    const exhausted = remaining === 0;
    const toggleOn = !exhausted && this.isToggleOn();

    return (
      <div class="contextTranslationStrip">
        <div class="contextTranslationStrip__inner">
          <span class="contextTranslationStrip__label">
            Context-aware translations
          </span>
          <button
            type="button"
            class={cn({
              contextTranslationStrip__toggle: true,
              "is-on": toggleOn,
              "is-disabled": exhausted,
            })}
            role="switch"
            aria-checked={toggleOn}
            aria-label="Toggle context-aware translations"
            onClick={this.onToggleClick}
          >
            <span class="contextTranslationStrip__toggleKnob" />
          </button>
          <span class="contextTranslationStrip__counter">
            <strong>
              {remaining}/{limit}
            </strong>{" "}
            left today
          </span>
          <a
            href="#"
            data-bypass="true"
            class="contextTranslationStrip__learnMore"
            onClick={this.onLearnMoreClick}
          >
            learn more
          </a>
        </div>
      </div>
    );
  }
}
